// src/components/sales/hooks/useSaleForm.ts

import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { addSale, fetchSales } from '../../../features/sales/salesSlice';
import { fetchVendors } from '../../../features/vendors/vendorSlice';
import { SaleFormValues } from '../types';
import { validateSaleForm } from '../validation';
import { useFormValidation } from '../../../hooks/useFormValidation';

const initialValues: SaleFormValues = {
  crop_id: '',
  vendor_id: '',
  quantity: '',
  unit: 'kg',
  unit_price: '',
  price_unit: 'per_kg', // will be overwritten on submit
  purchase_price: '',
  sale_date: new Date().toISOString().split('T')[0],
};

export const useSaleForm = (onSuccess: () => void) => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { values, errors, handleChange, handleSubmit, isValid } =
    useFormValidation(initialValues, validateSaleForm, async (values) => {
      setIsSubmitting(true);
      setSubmitError(null);

      try {
        // build the payload with correct numeric types and dynamic price_unit
        const payload = {
          ...values,
          quantity: Number(values.quantity),
          unit_price: Number(values.unit_price),
          purchase_price: values.purchase_price
            ? Number(values.purchase_price)
            : undefined,
          price_unit: `per_${values.unit}`, // per_kg, per_quintal, etc.
        };

        await dispatch(addSale(payload));

        await Promise.all([dispatch(fetchSales()), dispatch(fetchVendors())]);

        onSuccess();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to add sale';
        setSubmitError(errorMessage);
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    });

  return {
    values,
    errors,
    handleChange,
    handleSubmit,
    isValid,
    isSubmitting,
    submitError,
  };
};
